import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Paper,
  IconButton,
  Tooltip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import CryptoJS from 'crypto-js';
import { AuditLoggers } from '../../../utils/auditLogger';
import { Search as SearchIcon } from '@mui/icons-material';
import {
  DataGridPro,
} from '@mui/x-data-grid-pro';
import {
  Warning as WarningIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

// Import the common toast components
import Toast from '../../common/Toast';
import { useToast } from '../../hooks/useToast';
import { formatDate, formatDateTime } from '../../../utils/global'
import { useUserRights } from '../../../context/UserRightsContext'
import { hasWorkflowProductPermission } from './workflowProductScope';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateUploadOutlinedSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateGradientFormFieldsSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogCloseIconSx,
  harmonateDialogContentSx,
  harmonateFormFieldsSx,
  harmonateSectionTitleSx,
} from '../../../theme/harmonateStyles';
import { isHarmonateUser as isServerHarmonateUser } from '../../../auth/serverIdentity';

const harmonateBreadcrumbSx = {
  fontSize: '0.9rem',
  '& .MuiBreadcrumbs-li': { color: ht.white },
  '& a': { color: ht.white },
  '& .MuiBreadcrumbs-separator': { color: ht.white },
};

const UnClassifiedWorkflowDrawer = ({ 
  selectedDocumentType, 
  selectedWorkflowId, 
  onFileClick, 
  selectedQuarter,
  onBackToWorkflows,
  productScope,
}) => {
  // Side drawer API state
  const [drawerFiles, setDrawerFiles] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Audit logs state
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [documentDetails, setDocumentDetails] = useState(null);
  
  // Delete functionality state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowsToDelete, setRowsToDelete] = useState([]);
  const [deleteAllConfirmOpen, setDeleteAllConfirmOpen] = useState(false);
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Use the common toast hook
  const { toastState, showSuccess, showError, showInfo, hideToast } = useToast();
  const { hasPermission, isHarmonateUser } = useUserRights();

  // Fetch file instances for selected unclassified workflow
  const fetchFileInstances = async (quarter = selectedQuarter, suppressSuccessToast = false) => {
    try {
      setDrawerLoading(true);
      setDrawerError(null);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!tenantId) {
        throw new Error('No tenant ID found');
      }

      console.log(`Fetching unclassified file instances for tenant ID: ${tenantId}`);
      console.log(`Using backend URL: ${process.env.REACT_APP_BACKEND_URL}`);

      // Construct URL with tenantId and date range
      let url = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/unidentified/by-tenant/${tenantId}`;
    // let url = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/unidentified/by-tenant/${tenantId}`;
      const params = new URLSearchParams();
      
      // Add from/to params for quarter filtering
      // Skip date parameters if "All Quarters" is selected
      if (quarter === 'all') {
        // Don't add date parameters - fetch all data
      } else if (quarter && quarter.from && quarter.to) {
        params.append('from', quarter.from);
        params.append('to', quarter.to);
      }
      
      // Append query parameters to URL
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      console.log(`Making request to: ${url}`);
      
      // Create headers
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
      
      console.log(`Headers being sent:`, headers);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
      });

      console.log(`Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error text:', errorText);
        throw new Error(`Failed to fetch unclassified file instances: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const data = await response.json();
      console.log('API Response for unclassified tenant workflow:', data);
      
      // Transform API data based on the response structure
      const transformedFiles = data.map((file) => ({
        id: file.id,
        name: file.name || file['unitOfWorkId'], // Fallback to unitOfWorkId if name is missing
        fund: file.fundName || '',
        processStep:file.processStep,
        createdAt: file.createdAt || file.uploadAt,
         username: file.username || '', // Add username field
        fileInstanceId: file.id,
        fastApiFileInstanceId: file.integrationId || null,
        unitOfWorkId: file.unitOfWorkId,
        approvedStatus: file.approvedStatus || null
      }));

      // Sort by createdAt descending
      transformedFiles.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      console.log('Transformed Files for unclassified workflow:', transformedFiles);
      setDrawerFiles(transformedFiles);
      
      if (transformedFiles.length === 0) {
        showInfo('No unclassified file instances found for this tenant');
      } else if (!suppressSuccessToast) {
        showSuccess(`Successfully loaded ${transformedFiles.length} unclassified file instances`);
      }
    } catch (err) {
      console.error('Error fetching unclassified workflow file instances:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setDrawerError(err.message);
      setDrawerFiles([]);
      showError(`Failed to load unclassified workflow files: ${err.message}`, 'Loading Error');
    } finally {
      setDrawerLoading(false);
    }
  };

  // Fetch file instances when the component mounts or when the selectedQuarter changes
  useEffect(() => {
    if (selectedQuarter) {
      fetchFileInstances(selectedQuarter);
    }
  }, [selectedQuarter]);

  useEffect(() => {
    return () => {
      setIsDeleteMode(false);
      setSelectedRows([]);
      setRowsToDelete([]);
      setDeleteAllConfirmOpen(false);
      setPasswordDialogOpen(false);
      setPassword('');
      setPasswordError('');
    };
  }, [selectedQuarter]);

  const handleFileClick = async (params) => {
    try {
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }

      // Log file view attempt
      try {
        const fileName = params.row.name || 'unknown';
        console.log('Logging file view attempt...', { fileId: params.row.id, fileName });
        await AuditLoggers.documentViewed(
          params.row.id,
          fileName,
          'unclassified' // Indicate this is an unclassified file
        );
      } catch (logError) {
        console.error('Error logging file view:', logError);
        // Continue even if logging fails
      }

      // Build the URL with documentId parameter
      let url = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/unidentified/by-tenant/${tenantId}?documentId=${params.row.id}`;
      
      // Add date range if available
      if (selectedQuarter && selectedQuarter !== 'all' && selectedQuarter.from && selectedQuarter.to) {
        url += `&from=${encodeURIComponent(selectedQuarter.from)}&to=${encodeURIComponent(selectedQuarter.to)}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch document status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // If document is not found in unidentified list, it might have been identified
      if (!data || !Array.isArray(data) || data.length === 0) {
        showInfo('This document has been identified and is no longer in the Unidentified list.');
        // Refresh the list to remove the document, suppress success toast
        if (selectedQuarter) {
          fetchFileInstances(selectedQuarter, true);
        }
        return;
      }
      
      const document = data[0];

      // Block viewing while processing is still in progress
      if (document.processStep === 'In Progress') {
        showInfo('This file is currently being processed. Please try again later.');
        return;
      }

      // Proceed with navigation for any other processStep value
      onFileClick({
        fileInstanceId: document.id,
        fastApiFileInstanceId: document.integrationId || null,
        fileName: document.name,
        id: document.id,
        unitOfWorkId: document.unitOfWorkId,
        approvedStatus: document.approvedStatus
      }, true);
      
    } catch (error) {
      console.error('Error checking document status:', error);
      showError('Failed to verify document status. Please try again.');
    }
  };

  const handleInfoClick = async (event, row) => {
    event.stopPropagation();
    try {
      setLogsLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      
      if (!token || !tenantId) {
        throw new Error('Authentication required');
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${row.id}/with-audit-logs?tenantId=${tenantId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.status}`);
      }
      
      const data = await response.json();
      setDocumentDetails(data.document);
      setAuditLogs(data.auditLogs || []);
      setIsLogsModalOpen(true);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      showError('Failed to load document details and audit logs');
    } finally {
      setLogsLoading(false);
    }
  };

  // Delete functionality
  const handleDeleteClick = (event, row) => {
    event.stopPropagation();
    setFileToDelete(row);
    setDeleteConfirmOpen(true);
  };

  // Password helpers (align with other drawers)
  const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

  const generateKey = () => {
    const keyBytes = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY);
    const hash = CryptoJS.SHA256(keyBytes);
    return CryptoJS.lib.WordArray.create(hash.words.slice(0, 8));
  };

  const encryptData = (data) => {
    if (!data) return '';
    try {
      const stringData = data.toString();
      const key = generateKey();
      const encrypted = CryptoJS.AES.encrypt(stringData, key, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      });
      return encrypted.toString();
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Encryption failed: ' + error.message);
    }
  };

  const verifyPassword = async () => {
    if (!password.trim()) {
      setPasswordError('Please enter your password');
      return false;
    }

    try {
      setIsVerifying(true);
      setPasswordError('');

      const username = localStorage.getItem('username');
      if (!username) {
        throw new Error('User session expired. Please log in again.');
      }

      const encryptedPassword = encryptData(password);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}auth/signin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username,
            password: encryptedPassword,
            applicationId: 1,
            application: "Conductor"
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Incorrect password');
      }

      return true;
    } catch (error) {
      console.error('Password verification failed:', error);
      setPasswordError(error.message || 'Failed to verify password');
      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete?.id) {
      showError('File ID not found');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Log file deletion attempt
      try {
        const fileName = fileToDelete.name || fileToDelete.fileName || fileToDelete.originalFileName || 'unknown';
        console.log('Logging file deletion attempt...', { fileToDelete, fileName });
        await AuditLoggers.fileDeletionInitiated(
          fileToDelete.id,
          fileName,
          'unclassified' // Since this is the unclassified workflow drawer
        );
      } catch (logError) {
        console.error('Error logging file deletion:', logError);
        // Continue with deletion even if logging fails
      }

      const deleteUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/${fileToDelete.id}/delete`;
      console.log('Calling delete API at URL:', deleteUrl);

      const response = await fetch(deleteUrl, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Delete response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete error response text:', errorText);
        throw new Error(`Failed to delete file: ${response.status} - ${errorText}`);
      }

      showSuccess('File deleted successfully');
      setDeleteConfirmOpen(false);
      setFileToDelete(null);
      
      // Refresh file instances
      if (selectedQuarter) {
        fetchFileInstances(selectedQuarter);
      }
    } catch (error) {
      console.error('Error during file deletion:', error);
      showError(`Failed to delete file: ${error.message}`);
    }
  };

  const handleRetry = () => {
    if (selectedQuarter) {
      fetchFileInstances(selectedQuarter);
    }
  };

  const handleRowSelection = (newSelection) => {
    setSelectedRows(newSelection);
  };

  const handleDeleteAllClick = (e) => {
    e?.preventDefault();

    if (selectedRows.length === 0) {
      if (!isDeleteMode) {
        setIsDeleteMode(true);
      }
      return;
    }

    setRowsToDelete([...selectedRows]);
    setDeleteAllConfirmOpen(true);
  };

  const handleCancelDeleteMode = () => {
    setIsDeleteMode(false);
    setSelectedRows([]);
    setRowsToDelete([]);
    setDeleteAllConfirmOpen(false);
    setPasswordDialogOpen(false);
    setPassword('');
    setPasswordError('');
  };

  const executeBulkDelete = async () => {
    try {
      setIsBulkDeleting(true);
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
       // Get file names for logging
    const fileNames = rowsToDelete.map(id => {
      const file = drawerFiles.find(f => f.id === id);
      return file?.name || 'Unknown';
    });
    // Log the bulk delete attempt
    await AuditLoggers.bulkFileDeletionInitiated(
      rowsToDelete,
      fileNames,
      'unclassified',
      { 
        documentTypeId: selectedDocumentType
      }
    );
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/file-instances/bulk-delete`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(rowsToDelete)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete files');
      }

      const result = await response.json();
      showSuccess(`Successfully deleted ${result.deletedCount} files`);
      setSelectedRows([]);
      setRowsToDelete([]);
      setIsDeleteMode(false);
      handleRetry(); // Refresh the list
    } catch (error) {
      console.error('Error deleting files:', error);
      showError(error.message || 'Failed to delete files');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkDelete = () => {
    if (rowsToDelete.length === 0) {
      showError('No files selected for deletion');
      setDeleteAllConfirmOpen(false);
      return;
    }

    setDeleteAllConfirmOpen(false);
    setPassword('');
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  const handlePasswordConfirm = async () => {
    const isValid = await verifyPassword();
    if (isValid) {
      setPasswordDialogOpen(false);
      setPassword('');

      await executeBulkDelete();
    }
  };

  const fallbackHarmonate = isServerHarmonateUser();
  const effectiveIsHarmonate = typeof isHarmonateUser === 'boolean' ? isHarmonateUser : fallbackHarmonate;
  const canDeleteWorkflow = hasWorkflowProductPermission(
    hasPermission,
    effectiveIsHarmonate,
    productScope,
    'delete'
  );

  const columns = [
    { field: 'name', headerName: 'File Name', flex: 1, resizable: true },
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 1,
      width: 200,
      resizable: true,
      renderCell: (params) => (
        <Typography sx={{ fontSize: '0.7rem' }}>
          {params.value ? formatDateTime(params.value) : ''}
        </Typography>
      ),
    },
    { field: 'fund', headerName: 'Fund', width: 300, resizable: true },
    { field: 'username', headerName: 'Uploaded By', width: 150, resizable: true },
    { field: 'processStep', headerName: 'Process Step', width: 130, resizable: true },
    { 
      field: 'actions',
      headerName: '',
      width: 100,
      resizable: true,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => '',
      renderCell: (params) => (
           <>
             {effectiveIsHarmonate && (
                <Tooltip title="View details">
                  <IconButton
                    size="small"
                    onClick={(e) => handleInfoClick(e, params.row)}
                    sx={{ color: 'primary.main' }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDeleteWorkflow && (
                <Tooltip title="Delete file">
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(e, params.row)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          ),
        },
      ];

  const filteredFiles = drawerFiles.filter((file) => {
    const fileName = file.name.toLowerCase();
    const fundName = file.fund.toLowerCase();
    const searchQueryLower = searchQuery.toLowerCase();
    return fileName.includes(searchQueryLower) || fundName.includes(searchQueryLower);
  });

  return (
    <>
      <Box sx={{ ...harmonatePageSx, minHeight: '100vh' }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={harmonateBreadcrumbSx}>
            <Link
              underline="hover"
              color="inherit"
              onClick={onBackToWorkflows}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <HomeIcon sx={{ fontSize: '1rem' }} />
              Workflows
            </Link>
            <Typography sx={{ color: ht.white, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <WorkIcon sx={{ fontSize: '1rem' }} />
              {selectedDocumentType}
            </Typography>
          </Breadcrumbs>
        </Box>
        
        {/* Search & Delete Controls */}
        <Box
          component="form"
          autoComplete="off"
          noValidate
          onSubmit={(e) => e.preventDefault()}
          sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}
        >
          <TextField
            label="Search Files or Funds"
            variant="outlined"
            size="medium"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ 
              width: 350,
              ...harmonateGradientFormFieldsSx,
              '& .MuiInputBase-root': {
                fontSize: '0.9rem',
                height: 48,
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.9rem',
                color: ht.white,
                '&.Mui-focused': { color: ht.white },
                '&.MuiInputLabel-shrink': { color: ht.white },
              },
            }}
            placeholder="Enter file name or fund name"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: ht.white }} />
                </InputAdornment>
              ),
            }}
          />
          {canDeleteWorkflow && (
            <>
              <Button
                variant={isDeleteMode ? "outlined" : "contained"}
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteAllClick}
                disabled={!canDeleteWorkflow}
                sx={{ height: 48, minWidth: 160 }}
                type="button"
              >
                {!isDeleteMode
                  ? 'Bulk Delete'
                  : selectedRows.length > 0
                    ? `Delete Selected (${selectedRows.length})`
                    : 'Select files'}
              </Button>
              {isDeleteMode && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={handleCancelDeleteMode}
                  sx={{ height: 48, minWidth: 140 }}
                  type="button"
                >
                  Cancel
                </Button>
              )}
            </>
          )}
        </Box>

        {/* Content */}
        <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, flex: 1, overflow: 'hidden' }}>
          {/* Loading state */}
          {drawerLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
              <CircularProgress sx={{ color: ht.green }} />
              <Typography variant="body1" sx={{ ml: 2 }}>Loading unclassified workflow files...</Typography>
            </Box>
          ) : drawerError ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '400px', justifyContent: 'center' }}>
              <Alert severity="error" sx={{ mb: 2, maxWidth: '500px' }}>
                <Typography>Failed to load unclassified workflow files: {drawerError}</Typography>
              </Alert>
              <Button 
                variant="outlined" 
                onClick={handleRetry}
                sx={harmonateUploadOutlinedSx}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <DataGridPro
              rows={filteredFiles}
              columns={columns}
              rowHeight={40}
              onRowClick={handleFileClick}
              checkboxSelection={isDeleteMode}
              onRowSelectionModelChange={handleRowSelection}
              rowSelectionModel={isDeleteMode ? selectedRows : []}
              disableRowSelectionOnClick
              pagination
              rowsPerPageOptions={[10, 25, 50]}
              pageSize={10}
              autoPageSize={false}
              sx={{ 
                ...harmonateDataGridSx,
                height: 700,
                '& .MuiDataGrid-cell': {
                  fontSize: '0.7rem',
                },
                '& .MuiDataGrid-columnHeader': {
                  fontSize: '0.9rem',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-row:hover': { 
                  cursor: 'pointer' 
                }
              }}
              initialState={{
                filter: {
                  filterModel: {
                    items: [
                      { columnField: 'name', operatorValue: 'contains', value: '' },
                      { columnField: 'date', operatorValue: 'equals', value: null },
                    ],
                  },
                },
              }}
              getRowId={(row) => row.id}
              noRowsOverlay={() => (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {searchQuery ? 'No files match your search' : 'No file instances found for this workflow'}
                  </Typography>
                </Box>
              )}
            />
          )}
        </Paper>
      </Box>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog
        open={deleteAllConfirmOpen}
        onClose={() => !isBulkDeleting && setDeleteAllConfirmOpen(false)}
        aria-labelledby="delete-all-dialog-title"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx} id="delete-all-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete {rowsToDelete.length} selected file(s)? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteAllConfirmOpen(false)}
            disabled={isBulkDeleting}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            variant="contained"
            disabled={isBulkDeleting}
            startIcon={isBulkDeleting ? <CircularProgress size={20} color="inherit" /> : null}
            sx={harmonateAbortButtonSx}
          >
            {isBulkDeleting ? 'Processing...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Confirmation Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => !isVerifying && setPasswordDialogOpen(false)}
        aria-labelledby="password-dialog-title"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx} id="password-dialog-title">Confirm Your Identity</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogContentSx, ...harmonateFormFieldsSx }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Please enter your password to confirm deletion of {rowsToDelete.length} file(s).
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Password"
            type="password"
            autoComplete="off"
            fullWidth
            variant="outlined"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setPasswordError('');
            }}
            error={!!passwordError}
            helperText={passwordError}
            disabled={isVerifying}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handlePasswordConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPasswordDialogOpen(false);
              setPassword('');
              setPasswordError('');
            }}
            disabled={isVerifying}
            variant="outlined"
            sx={harmonateCancelButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePasswordConfirm}
            variant="contained"
            disabled={isVerifying || !password.trim()}
            startIcon={isVerifying ? <CircularProgress size={20} color="inherit" /> : null}
            sx={harmonateAbortButtonSx}
          >
            {isVerifying ? 'Verifying...' : 'Confirm Deletion'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx} id="delete-dialog-title">
          Confirm File Deletion
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography>
            Are you sure you want to delete this file? This action cannot be undone.
          </Typography>
          {fileToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2"><strong>File Name:</strong> {fileToDelete.name}</Typography>
              <Typography variant="body2"><strong>File ID:</strong> {fileToDelete.id}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            sx={harmonateAbortButtonSx}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Details & Audit Logs Modal */}
      {isLogsModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          }}
          onClick={() => setIsLogsModalOpen(false)}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            elevation={0}
            sx={{
              width: '95%',
              maxWidth: '1200px',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              overflow: 'hidden',
              ...harmonateCardSx,
              bgcolor: ht.white,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${ht.line}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: ht.green,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, flexGrow: 1, color: ht.white, fontSize: '1.1rem' }}
              >
                Document Details & Audit Logs
              </Typography>
              <IconButton
                size="small"
                onClick={() => setIsLogsModalOpen(false)}
                sx={harmonateDialogCloseIconSx}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {logsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={24} sx={{ color: ht.green }} />
              </Box>
            ) : (
              <Box sx={{ p: 2, overflow: 'auto', flex: 1, bgcolor: ht.listBg }}>
                {/* Document Details Section */}
                {documentDetails && (
                  <Box sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: ht.white,
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                    boxShadow: ht.cardShadow,
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1.5,
                      pb: 1,
                      borderBottom: `1px solid ${ht.line}`,
                    }}>
                      <Typography variant="subtitle2" sx={harmonateSectionTitleSx}>
                        DOCUMENT INFORMATION
                      </Typography>
                    </Box>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      gap: 2,
                      '& > div': {
                        minWidth: 0, // Prevents overflow
                      }
                    }}>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Document Name</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text, fontWeight: 500 }}>{documentDetails.name || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Document Type</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>{documentDetails.documentTypeId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Uploaded At</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>
                          {documentDetails.uploadAt ? new Date(documentDetails.uploadAt).toLocaleString() :
                           documentDetails.createdAt ? new Date(documentDetails.createdAt).toLocaleString() : 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Location</Typography>
                        <Tooltip
                          title={documentDetails.location || 'N/A'}
                          arrow
                          placement="top"
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              maxWidth: '300px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              display: 'inline-block',
                              verticalAlign: 'bottom',
                              fontSize: '0.875rem',
                              color: ht.text,
                              cursor: 'pointer',
                              '&:hover': {
                                textDecoration: 'underline',
                                textDecorationColor: ht.text,
                                textDecorationThickness: '1px',
                                textUnderlineOffset: '2px',
                              }
                            }}
                          >
                            {documentDetails.location || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Unit of Work</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>
                          {documentDetails.unitOfWorkId || 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" display="block" sx={{ color: ht.text }}>Integration ID</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text }}>{documentDetails.integrationId || 'N/A'}</Typography>
                      </div>
                    </Box>
                  </Box>
                )}

                {/* Audit Logs Section */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: ht.white,
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                    boxShadow: ht.cardShadow,
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1.5,
                    pb: 1,
                    borderBottom: `1px solid ${ht.line}`,
                  }}>
                    <Typography variant="subtitle2" sx={harmonateSectionTitleSx}>
                      AUDIT LOGS
                    </Typography>
                  </Box>
                  {auditLogs.length > 0 ? (
                    <Box sx={{ height: 'calc(100% - 40px)', width: '100%' }}>
                      <DataGridPro
                        rows={auditLogs}
                        pagination
                        rowsPerPageOptions={[10, 25, 50]}
                        pageSize={10}
                        autoPageSize={false}
                        columns={[
                          { 
                            field: 'createdAt', 
                            headerName: 'Timestamp', 
                            flex: 1,
                            minWidth: 160,
                            resizable: true,
                            valueFormatter: (params) => params.value ? new Date(params.value).toLocaleString() : 'N/A',
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Timestamp
                              </Typography>
                            )
                          },
                          {
                            field: 'service',
                            headerName: 'Service',
                            flex: 0.8,
                            minWidth: 150,
                            resizable: true,
                            cellClassName: 'service-cell',
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Service
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Box sx={{
                                bgcolor: params.value === 'upload' ? ht.secondaryTab :
                                  params.value === 'document_classification' ? 'rgba(2, 107, 29, 0.12)' :
                                    params.value === 'extraction' ? 'rgba(46, 166, 160, 0.18)' :
                                      params.value === 'ingest' ? 'rgba(1, 48, 98, 0.12)' : ht.listBg,
                                color: params.value === 'upload' ? ht.text :
                                  params.value === 'document_classification' ? ht.green :
                                    params.value === 'extraction' ? ht.blue :
                                      params.value === 'ingest' ? ht.text : ht.text,
                                px: 1.5,  // Increased horizontal padding
                                py: 0.5, // Increased vertical padding
                                borderRadius: ht.buttonRadius,
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                minWidth: 'fit-content',
                                width: 'auto',
                                whiteSpace: 'nowrap',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                {params.value || 'N/A'}
                              </Box>
                            )
                          },
                          {
                            field: 'action',
                            headerName: 'Action',
                            flex: 1,
                            minWidth: 120,
                            resizable: true,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Action
                              </Typography>
                            ),
                            valueFormatter: (params) => params.value || 'N/A',
                            renderCell: (params) => (
                              <Box sx={{
                                color: ht.text,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                fontFamily: 'monospace'
                              }}>
                                {params.formattedValue}
                              </Box>
                            )
                          },
                          {
                            field: 'status',
                            headerName: 'Status',
                            flex: 0.8,
                            minWidth: 100,
                            resizable: true,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Status
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Box sx={{
                                color: ht.text,
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'uppercase'
                              }}>
                                {params.value || 'N/A'}
                              </Box>
                            )
                          },
                          {
                            field: 'details',
                            headerName: 'Details',
                            flex: 2,
                            minWidth: 200,
                            resizable: true,
                            renderHeader: () => (
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: ht.text }}>
                                Details
                              </Typography>
                            ),
                            renderCell: (params) => (
                              <Tooltip
                                title={params.value ?
                                  (typeof params.value === 'object' ?
                                    JSON.stringify(params.value, null, 2) :
                                    params.value.toString()) :
                                  'N/A'}
                                arrow
                                placement="top"
                              >
                                <Box sx={{
                                  width: '100%',
                                  '&:hover': {
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationColor: ht.text,
                                    textDecorationThickness: '1px',
                                    textUnderlineOffset: '2px',
                                  }
                                }}>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    sx={{
                                      color: ht.text,
                                      fontSize: '0.8rem',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {params.value ?
                                      (typeof params.value === 'object' ?
                                        JSON.stringify(params.value) :
                                        params.value.toString()
                                      ).substring(0, 50) + (params.value.length > 50 ? '...' : '') :
                                      'N/A'}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )
                          }
                        ]}
                        disableSelectionOnClick
                        disableColumnMenu
                        hideFooterSelectedRowCount
                        sx={{
                          ...harmonateDataGridSx,
                          border: 'none',
                          '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: ht.listBg,
                            borderBottom: `1px solid ${ht.line}`,
                            minHeight: '40px !important',
                            maxHeight: '40px !important',
                          },
                          '& .MuiDataGrid-columnHeader': {
                            py: 1,
                          },
                          '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            color: ht.text,
                          },
                          '& .MuiDataGrid-cell': {
                            fontSize: '0.8125rem',
                            color: ht.text,
                            py: 1,
                            borderColor: ht.line,
                          },
                          '& .MuiDataGrid-row': {
                            '&:hover': {
                              backgroundColor: ht.secondaryTab,
                            },
                            '&.Mui-selected': {
                              backgroundColor: ht.secondaryTab,
                              '&:hover': {
                                backgroundColor: ht.secondaryTab,
                              },
                            },
                          },
                          '& .MuiDataGrid-footerContainer': {
                            borderTop: `1px solid ${ht.line}`,
                            minHeight: '48px',
                          },
                        }}
                        components={{
                          LoadingOverlay: LinearProgress,
                          NoRowsOverlay: () => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body2" sx={{ color: ht.text }}>
                                No audit logs available
                              </Typography>
                            </Box>
                          ),
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: ht.text }}>
                      No audit logs available for this document.
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Common Toast Component */}
      <Toast 
        open={toastState.open}
        onClose={hideToast}
        message={toastState.message}
        severity={toastState.severity}
        title={toastState.title}
        autoHideDuration={toastState.autoHideDuration}
        position={toastState.position}
        variant={toastState.variant}
      />
    </>
  );
};

export default UnClassifiedWorkflowDrawer;